import Button, { ButtonProps, ButtonStandardSize } from '@/storybook/components/Button/Button';
import MultiLevelSelect, {
  MultiLevelSelectProps,
} from '@/storybook/components/FormCollection/MultiLevelSelect/MultiLevelSelect';
import MultiSelect, { MultiSelectProps } from '@/storybook/components/FormCollection/MultiSelect/MultiSelect';
import SingleDateFilter, {
  SingleDateFilterProps,
} from '@/storybook/components/FormCollection/SingleDateFilter/SingleDateFilter';
import SingleLevelSelect, {
  SingleLevelSelectProps,
} from '@/storybook/components/FormCollection/SingleLevelSelect/SingleLevelSelect';
import SingleSelect, { SingleSelectProps } from '@/storybook/components/FormCollection/SingleSelect/SingleSelect';
import TreeSelect from '@/storybook/components/FormCollection/TreeSelect/TreeSelect';
import { TreeSelectProps } from '@/storybook/components/FormCollection/TreeSelect/TreeSelect.types';
import { DropdownButtonSize } from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import Switch, { SwitchProps } from '@/storybook/components/Switch/Switch';
import Text, { ValidHtmlElement } from '@/storybook/components/Text/Text';
import { DownloadButtonProps } from '@/types/download/DownloadButtonProps';
import cx from 'classnames';
import { ReactNode } from 'react';
import DownloadDataButton from '../DownloadDataButton/DownloadDataButton';
import Styles from './SectionTitle.module.css';

export type SectionTitleSize = 'S' | 'M' | 'L';

const SECTION_TITLE_HEADING_TAG: Record<SectionTitleSize, ValidHtmlElement> = {
  L: 'h2',
  M: 'h4',
  S: 'h4',
};

const SECTION_TITLE_DESCRIPTION_TAG: Record<SectionTitleSize, ValidHtmlElement> = {
  L: 'p',
  M: 'small',
  S: 'small',
};

const SECTION_TITLE_BUTTON_SIZE: Record<SectionTitleSize, ButtonStandardSize> = {
  L: 'L',
  M: 'S',
  S: 'S',
};

const SECTION_TITLE_DROPDOWN_SIZE: Record<SectionTitleSize, keyof typeof DropdownButtonSize> = {
  L: 'L',
  M: 'M',
  S: 'M',
};

const SECTION_TITLE_DROPDOWN_OPTIONS: Record<string, Function> = {
  singleSelect: (props: SingleSelectProps) => <SingleSelect {...props} />,
  multiSelect: (props: MultiSelectProps) => <MultiSelect {...props} />,
  singleLevelSelect: (props: SingleLevelSelectProps) => <SingleLevelSelect {...props} />,
  multiLevelSelect: (props: MultiLevelSelectProps) => <MultiLevelSelect {...props} />,
  treeSelect: (props: TreeSelectProps<string>) => <TreeSelect {...props} />,
  singleDateFilter: (props: SingleDateFilterProps) => <SingleDateFilter {...props} />,
};

type SingleSelectDropDownProps<T = string> = {
  type: 'singleSelect';
  props: SingleSelectProps<T>;
};

type MultiSelectDropDownProps<T = string> = {
  type: 'multiSelect';
  props: MultiSelectProps<T>;
};

type SingleLevelSelectDropDownProps<T = string> = {
  type: 'SingleLevelSelect';
  props: SingleLevelSelectProps<T>;
};

type MultiLevelSelectDropDownProps<T = string> = {
  type: 'multiLevelSelect';
  props: MultiLevelSelectProps<T>;
};

type TreeSelectDropDownProps<T = string> = {
  type: 'treeSelect';
  props: TreeSelectProps<T>;
};

type SingleDateFilterDropdownProps = {
  type: 'singleDateFilter';
  props: SingleDateFilterProps;
};

export type SectionTitleButtonProps = Omit<ButtonProps, 'iconOnly' | 'icon'> & {
  label?: string;
  icon?: ReactNode;
  iconOnly?: boolean;
};

export type SectionTitleDropdown<T = string> =
  | SingleSelectDropDownProps<T>
  | MultiSelectDropDownProps<T>
  | SingleLevelSelectDropDownProps<T>
  | MultiLevelSelectDropDownProps<T>
  | TreeSelectDropDownProps<T>
  | SingleDateFilterDropdownProps;

export type SectionTitleProps<T> = {
  'size'?: SectionTitleSize;
  'title': ReactNode;
  'toggle'?: SwitchProps;
  'dropdown'?: SectionTitleDropdown<T>;
  'button'?: SectionTitleButtonProps;
  'downLoadButton'?: DownloadButtonProps;
  'description'?: ReactNode;
  'data-testid'?: string;
  'header-testid'?: string;
  'title-testid'?: string;
  'toggle-testid'?: string;
  'dropdown-testid'?: string;
  'button-testid'?: string;
  'download-button-testid'?: string;
  'description-testid'?: string;
  'className'?: string;
  'ctaContainerAdditionalStyles'?: string;
};

export default function SectionTitle<T>({
  title,
  size = 'L',
  description,
  toggle,
  dropdown,
  button,
  downLoadButton,
  className,
  ctaContainerAdditionalStyles,
  'data-testid': dataTestId,
  'header-testid': headerTestId,
  'title-testid': titleTestId,
  'toggle-testid': toggleTestId,
  'dropdown-testid': dropdownTestId,
  'button-testid': buttonTestId,
  'download-button-testid': downloadButtonTestId,
  'description-testid': descriptionTestId,
}: SectionTitleProps<T>) {
  return (
    <div className={cx(Styles.sectionTitleContainer, className)} data-size={size} data-testid={dataTestId}>
      <section className={Styles.titleAndCtaContainer} data-testid={headerTestId}>
        <Text as={SECTION_TITLE_HEADING_TAG[size]} data-testid={titleTestId || `${dataTestId}-title`}>
          {title}
        </Text>
        <span className={cx(Styles.ctaContainer, ctaContainerAdditionalStyles)}>
          {toggle && <Switch {...toggle} data-testid={toggleTestId || `${dataTestId}-toggle`} />}
          {dropdown &&
            SECTION_TITLE_DROPDOWN_OPTIONS[dropdown.type]({
              ...dropdown.props,
              'alignment': 'horizontal',
              'className': Styles.dropdown,
              'data-testid': dropdownTestId || `${dataTestId}-dropdown`,
              'size': SECTION_TITLE_DROPDOWN_SIZE[size],
            })}
          {button && button.iconOnly && (
            <Button
              className={button.className}
              size="L"
              variant="text"
              onClick={button.onClick}
              data-testid={buttonTestId || `${dataTestId}-button`}
              iconOnly={true}
              icon={button.icon}
            />
          )}
          {button && !button.iconOnly && (
            <Button
              className={button.className}
              size={SECTION_TITLE_BUTTON_SIZE[size]}
              variant="outlined"
              onClick={button.onClick}
              data-testid={buttonTestId || `${dataTestId}-button`}
            >
              {button.label}
            </Button>
          )}
          {downLoadButton && (
            <DownloadDataButton
              {...downLoadButton}
              data-testid={downloadButtonTestId || `${dataTestId}-downLoadButton`}
            />
          )}
        </span>
      </section>
      {description && (
        <Text
          as={SECTION_TITLE_DESCRIPTION_TAG[size]}
          className={Styles.description}
          data-size={size}
          data-testid={descriptionTestId || `${dataTestId}-description`}
        >
          {description}
        </Text>
      )}
    </div>
  );
}
